import { GeoJSONFeature } from "mapbox-gl";
import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";
import { Layer, Source, useMap } from "react-map-gl";
import { useDrawMapStore, useFavoriteStore, useMapStore, usePipedriveStore } from "../..";
import useDealsWithFocusArea from "../../hooks/useDealsWithFocusArea";
import useDealsWithGlobalFilters from "../../hooks/useDealsWithGlobalFilters";
import useGetDealColor from "../../hooks/useGetDealColor";
import useNavigateToDeal from "../../hooks/useNavigateToDeal";
import BaseMap from "../../interfaces/BaseMap";
import IDeal, { DealStatus } from "../../interfaces/IDeal";
import { FavoriteTargetType } from "../../interfaces/IFavorite";
import GeometryService from "../../services/geometryService";
import { minZoom } from "../mapConfig";

export const wonDealPoligonLayerId = "wonDealPoligonLayer";
export const lostDealPoligonLayerId = "lostDealPoligonLayer";
export const openDealPoligonLayerId = "openDealPoligonLayer";
const dealPolygonLayerIds = [wonDealPoligonLayerId, lostDealPoligonLayerId, openDealPoligonLayerId];

function DealsMapLayer({ wonVisible, lostVisible, openVisible }: IDealsMapLayerProps) {
    const map = useMap().current!;
    const navigateToDeal = useNavigateToDeal();
    const mapStore = useMapStore();
    const drawMapStore = useDrawMapStore();
    const pipedriveStore = usePipedriveStore();
    const deals = useDealsWithFocusArea(useDealsWithGlobalFilters(pipedriveStore.deals));
    const visibleDeals = useMemo(() => computed(() => pipedriveStore.editedDealId ? deals.filter(d => d.id !== pipedriveStore.editedDealId) : deals), [deals]).get();
    const lostDeals = useMemo(() => visibleDeals.filter(d => lostVisible && d.status === DealStatus.Lost), [visibleDeals, lostVisible]);
    const openDeals = useMemo(() => visibleDeals.filter(d => openVisible && d.status === DealStatus.Open), [visibleDeals, openVisible]);
    const wonDeals = useMemo(() => visibleDeals.filter(d => wonVisible && d.status === DealStatus.Won), [visibleDeals, wonVisible]);
    const [hoveredFeature, setHoveredFeature] = useState<GeoJSONFeature | null>(null);

    useEffect(() => {
        map.loadImage("./ico-fav-5.png", (error, image) => {
            if (error) throw error;
            // add image to the active style and make it SDF-enabled
            map.addImage("ico-fav", image!, { sdf: true });
        });
    }, []);

    useEffect(() => {
        const isItDealLayer = (feature: GeoJSONFeature | undefined) => feature?.layer?.id && dealPolygonLayerIds.includes(feature.layer.id);
        const isItInteractionWithDeal = (feature: GeoJSONFeature | undefined) => isItDealLayer(feature) && !drawMapStore.isActiveDrawing();

        const unsubscribeMouseMove = mapStore.onMouseMove("deals", (feature, e) => {
            if (!isItInteractionWithDeal(feature))
                return;

            hoveredFeature && e.target.setFeatureState(hoveredFeature, {
                hover: false,
                clicked: false,
            });

            setHoveredFeature(feature!);
            e.target.setFeatureState(feature!, {
                hover: true,
            });
        });

        const unsubscribeMouseLeave = mapStore.onMouseLeave("deals", (feature, e) => {
            if (!isItDealLayer(feature))
                return;

            setHoveredFeature(null);
            e.target.setFeatureState(feature!, {
                hover: false,
            });
        });

        const unsubscribeMouseDown = mapStore.onMouseDown("deals", (feature, e) => {
            if (!isItInteractionWithDeal(feature))
                return;

            e.target.setFeatureState(feature!, {
                clicked: true,
            });
        });

        const unsubscribeMouseUp = mapStore.onMouseUp("deals", (feature, e) => {
            if (!isItDealLayer(feature))
                return;

            e.target.setFeatureState(feature!, {
                clicked: false,
            });
        });

        const unsubscribeMouseClick = mapStore.onMouseClick("deals", feature => {
            if (isItInteractionWithDeal(feature))
                navigateToDeal(Number(feature!.id));
        });

        return () => {
            unsubscribeMouseMove();
            unsubscribeMouseLeave();
            unsubscribeMouseDown();
            unsubscribeMouseUp();
            unsubscribeMouseClick();
        };
    }, [hoveredFeature, navigateToDeal]);

    return (
        <>
            <DealsMapLayerInternal
                deals={lostDeals}
                identificator="lost"
                interactiveLayerId={lostDealPoligonLayerId}
            />

            <DealsMapLayerInternal
                deals={openDeals}
                identificator="open"
                interactiveLayerId={openDealPoligonLayerId}
            />

            <DealsMapLayerInternal
                deals={wonDeals}
                identificator="won"
                interactiveLayerId={wonDealPoligonLayerId}
            />
        </>
    );
}

export default observer(DealsMapLayer);

interface IDealsMapLayerProps {
    wonVisible: boolean;
    lostVisible: boolean;
    openVisible: boolean;
}

const DealsMapLayerInternal = observer(({ deals, identificator, interactiveLayerId }: IDealsMapLayerInternalProps) => {
    const mapStore = useMapStore();
    const pipedriveStore = usePipedriveStore();
    const favoriteStore = useFavoriteStore();
    const geometryService = useMemo(() => new GeometryService(), []);
    const getDealColor = useGetDealColor();

    const dealPointsGeoJson: any = useMemo(() => computed(() => ({
        type: "FeatureCollection",
        features: deals
            .map(d => ({
                type: "Feature",
                id: d.id,
                geometry: {
                    type: "Point",
                    coordinates: [d.lon, d.lat],
                },
                properties: {
                    color: getDealColor(d.status, pipedriveStore.dealStages.find(s => s.id === d.dealStageId)!.name),
                    isFavorite: favoriteStore.favorites.some(f => f.targetType === FavoriteTargetType.Deal && f.targetId === d.id),
                },
            })),
    })), [deals, getDealColor]).get();

    const dealPoligonsGeoJson: any = useMemo(() => computed(() => ({
        type: "FeatureCollection",
        features: deals
            .map(d => {
                const dealStage = pipedriveStore.dealStages.find(s => s.id === d.dealStageId)!;
                return {
                    ...geometryService.mapIShapesToMultiPolygonFeature(d.dealShapes),
                    id: d.id,
                    properties: {
                        isFilled: mapStore.baseMap === BaseMap.Streets,
                        color: getDealColor(d.status, dealStage.name),
                        darkColor: makeColorDark(getDealColor(d.status, dealStage.name), 50),
                    },
                };
            }),
    })), [deals, getDealColor]).get();

    if (!mapStore.isStyleLoaded)
        return null;

    return (
        <>
            <Source id={`${identificator}DealPoligonSource`} type="geojson" data={dealPoligonsGeoJson}>
                <Layer
                    id={interactiveLayerId}
                    type="fill"
                    minzoom={minZoom.deals}
                    paint={{
                        "fill-antialias": false,
                        "fill-color": ["case",
                            ["to-boolean", ["feature-state", "clicked"]], "#2E3192",
                            ["to-boolean", ["feature-state", "hover"]], "#8283BE",
                            ["get", "color"],
                        ],
                        "fill-opacity": ["case",
                            ["!", ["to-boolean", ["get", "isFilled"]]], 0,
                            1
                        ],
                    }}
                />
                <Layer
                    id={`${identificator}DealBorderLayer`}
                    type="line"
                    minzoom={minZoom.deals}
                    paint={{
                        "line-color": ["case",
                            ["to-boolean", ["feature-state", "clicked"]], "#2E3192",
                            ["to-boolean", ["feature-state", "hover"]], "#8283BE",
                            ["get", "darkColor"],
                        ],
                        "line-width": 5,
                    }}
                    layout={{
                        "line-cap": "round",
                        "line-join": "round",
                    }}
                />
            </Source>
            <Source id={`${identificator}DealPointSource`} type="geojson" data={dealPointsGeoJson}>
                <Layer
                    id={`${identificator}DealPointHaloLayer`}
                    type="symbol"
                    minzoom={minZoom.deals}
                    paint={{ "icon-color": "white" }}
                    layout={{
                        "icon-size": ["case",
                            ["to-boolean", ["get", "isFavorite"]], 0.3,
                            ["!", ["to-boolean", ["get", "isFavorite"]]], 1,
                            1,
                        ],
                        "icon-allow-overlap": true,
                        "symbol-z-order": "source",
                        "icon-image": ["case",
                            ["to-boolean", ["get", "isFavorite"]], "ico-fav",
                            ["!", ["to-boolean", ["get", "isFavorite"]]], "ico-circle",
                            "ico-circle",
                        ],
                    }}
                />
                <Layer
                    id={`${identificator}DealPointLayer`}
                    type="symbol"
                    minzoom={minZoom.deals}
                    paint={{ "icon-color": ["get", "color"] }}
                    layout={{
                        "icon-size": ["case",
                            ["to-boolean", ["get", "isFavorite"]], 0.25,
                            ["!", ["to-boolean", ["get", "isFavorite"]]], 0.9,
                            0.9,
                        ],
                        "icon-allow-overlap": true,
                        "symbol-z-order": "source",
                        "icon-image": ["case",
                            ["to-boolean", ["get", "isFavorite"]], "ico-fav",
                            ["!", ["to-boolean", ["get", "isFavorite"]]], "ico-circle",
                            "ico-circle",
                        ],
                    }}
                />
            </Source>
        </>
    );
});

interface IDealsMapLayerInternalProps {
    deals: IDeal[];
    identificator: string;
    interactiveLayerId: string;
}

const makeColorDark = (color: string, amount: number) => { // https://stackoverflow.com/a/57401891
    return "#" + color.replace(/^#/, "").replace(/../g, color => ("0" + Math.min(255, Math.max(0, parseInt(color, 16) - amount)).toString(16)).substr(-2));
};